import { useQuery } from "@tanstack/react-query";
import { Search, X } from "lucide-react";

import { api, type MarketplaceOut } from "@/lib/api";

export interface ActivityFilterState {
  kind: string;
  marketplace_id: string;
  success: string; // "" | "true" | "false"
  since: string;
  until: string;
  search: string;
}

export const EMPTY_FILTERS: ActivityFilterState = {
  kind: "",
  marketplace_id: "",
  success: "",
  since: "",
  until: "",
  search: "",
};

interface ActivityFiltersProps {
  state: ActivityFilterState;
  onChange: (next: ActivityFilterState) => void;
  onReset: () => void;
  onExport: () => void;
}

export function ActivityFilters({
  state,
  onChange,
  onReset,
  onExport,
}: ActivityFiltersProps): JSX.Element {
  const kinds = useQuery({ queryKey: ["activity-kinds"], queryFn: api.activityKinds });
  const marketplaces = useQuery<MarketplaceOut[]>({
    queryKey: ["marketplaces"],
    queryFn: api.marketplaces,
  });

  const update = <K extends keyof ActivityFilterState>(
    key: K,
    value: ActivityFilterState[K],
  ): void => onChange({ ...state, [key]: value });

  return (
    <div className="card-stack space-y-3 bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="control-shell flex h-10 flex-1 min-w-[220px] items-center gap-2 px-3">
          <Search className="ic text-ink-3" strokeWidth={1.6} aria-hidden="true" />
          <input
            value={state.search}
            onChange={(e) => update("search", e.target.value)}
            placeholder="Cerca SKU o messaggio…"
            className="w-full bg-transparent outline-none"
            aria-label="Cerca attività"
          />
        </div>
        <select
          value={state.kind}
          onChange={(e) => update("kind", e.target.value)}
          className="control-shell h-10 px-3"
          aria-label="Filtra per tipo"
        >
          <option value="">Tutti i tipi</option>
          {(kinds.data ?? []).map((k) => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <select
          value={state.marketplace_id}
          onChange={(e) => update("marketplace_id", e.target.value)}
          className="control-shell h-10 px-3"
          aria-label="Filtra per marketplace"
        >
          <option value="">Tutti i marketplace</option>
          {(marketplaces.data ?? []).map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
        <select
          value={state.success}
          onChange={(e) => update("success", e.target.value)}
          className="control-shell h-10 px-3"
          aria-label="Filtra per esito"
        >
          <option value="">Esito qualsiasi</option>
          <option value="true">OK</option>
          <option value="false">KO</option>
        </select>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="control-shell flex h-10 items-center gap-2 px-3 text-xs text-ink-3">
          Da
          <input
            type="datetime-local"
            value={state.since}
            onChange={(e) => update("since", e.target.value)}
            className="bg-transparent text-sm outline-none"
            aria-label="Data inizio"
          />
        </label>
        <label className="control-shell flex h-10 items-center gap-2 px-3 text-xs text-ink-3">
          A
          <input
            type="datetime-local"
            value={state.until}
            onChange={(e) => update("until", e.target.value)}
            className="bg-transparent text-sm outline-none"
            aria-label="Data fine"
          />
        </label>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onReset}>
          <X className="ic" strokeWidth={1.6} aria-hidden="true" /> Reset
        </button>
        <span className="flex-1" />
        <button type="button" className="btn btn-outline btn-sm" onClick={onExport}>
          Export CSV
        </button>
      </div>
    </div>
  );
}
